/**
 * Selection Bridge Service
 *
 * Bridges selection state between trace-viewer (viewport click-to-select)
 * and the Inspector's selectionService (scene explorer, gizmos, properties).
 *
 * This lives at the service level so it survives pane collapse/unmount on mobile.
 */

import type { ServiceDefinition, ISelectionService } from '@babylonjs/inspector';
import { SelectionServiceIdentity } from '@babylonjs/inspector';
import type { TraceViewerElement, SceneObject } from 'trace-viewer';
import { settingsStore } from './inspector-extension';

/** Module-level ref so other code can read the selection service. */
let selectionServiceRef: ISelectionService | null = null;

/** When true, the bridge ignores Inspector→viewport selection changes. */
let bridgeSuppressed = false;

/** Get the Inspector's selection service (available after inspector opens). */
export function getSelectionService(): ISelectionService | null {
    return selectionServiceRef;
}

/**
 * Temporarily suppress the selection bridge (Inspector → viewport direction).
 * Useful for programmatic selection changes that shouldn't affect the viewport.
 */
export function suppressSelectionBridge(suppressed: boolean): void {
    bridgeSuppressed = suppressed;
}

/**
 * Whether the bridge is currently suppressed. Callers that suppress around a
 * short operation should capture this first and restore it (rather than
 * unconditionally un-suppressing), so they don't clobber a suppression another
 * owner is holding (e.g. MultiSelectionService while a modifier key is down).
 */
export function isSelectionBridgeSuppressed(): boolean {
    return bridgeSuppressed;
}

function getViewerElement(): TraceViewerElement | null {
    return document.querySelector('trace-viewer');
}

function findSceneObjectForEntity(entity: unknown): SceneObject | null {
    const viewer = getViewerElement();
    if (!viewer?.traceScene) return null;

    const sceneObjects = viewer.traceScene.getSceneObjects();

    // Direct transform match
    for (const sceneObject of sceneObjects) {
        if (sceneObject.transform === entity) {
            return sceneObject;
        }
    }

    // Check descendants (entity might be a child mesh under the SceneObject's transform)
    if (entity && typeof entity === 'object' && 'parent' in entity) {
        for (const sceneObject of sceneObjects) {
            if (sceneObject.transform) {
                let current = entity.parent;
                while (current) {
                    if (current === sceneObject.transform) return sceneObject;
                    current = (current as { parent?: unknown }).parent;
                }
            }
        }
    }

    return null;
}

export const SelectionBridgeServiceDefinition: ServiceDefinition<[], [ISelectionService]> = {
    friendlyName: 'Selection Bridge',
    consumes: [SelectionServiceIdentity],

    factory: (selectionService: ISelectionService) => {
        selectionServiceRef = selectionService;

        // Suppress flag to prevent feedback loops between the two directions
        let suppressInspectorSelection = false;

        // Play mode severs the bridge in BOTH directions (Unity game-view
        // rule): runtime grabs/taps must not drive the editor's selection,
        // and tree selection must not paint editor feedback onto the
        // running scene. Runtime selection (outline on grab) is the
        // engine's own play-mode affordance and is unaffected.
        let inPlayMode = false;
        const handlePlayModeChanged = (event: Event) => {
            const playing = (event as CustomEvent<{ playMode: boolean }>).detail.playMode;
            const wasPlaying = inPlayMode;
            inPlayMode = playing;
            // Selections made in the tree during play were deliberately not
            // painted onto the running scene — so on stop the two sides are
            // diverged. One sync on the falling edge converges them; without
            // it they stay apart until the next selection change.
            if (wasPlaying && !playing) syncInspectorToViewport();
        };
        window.addEventListener('trace-viewer:playmode-changed', handlePlayModeChanged);

        // Viewport → Inspector: when trace-viewer selects an object, update selectionService
        const handleViewportSelection = (event: Event) => {
            if (inPlayMode) return;
            const detail = (event as CustomEvent<{ object?: SceneObject | null }>).detail;
            const sceneObject = detail?.object;
            if (sceneObject?.transform) {
                suppressInspectorSelection = true;
                selectionService.selectedEntity = sceneObject.transform;
                suppressInspectorSelection = false;
            } else if (sceneObject === null || sceneObject === undefined) {
                selectionService.selectedEntity = null;
            }
        };

        // Inspector → Viewport: when Inspector selects an entity, update trace-viewer
        function syncInspectorToViewport(): void {
            if (suppressInspectorSelection || bridgeSuppressed || inPlayMode) return;

            const entity = selectionService.selectedEntity;
            if (entity) {
                const sceneObject = findSceneObjectForEntity(entity);
                if (sceneObject) {
                    // In debug mode, check if the entity IS the SceneObject's own
                    // transform. If not, the user selected a descendant node — skip
                    // the viewport sync so the inspector stays on that node.
                    if (settingsStore.debugSelectAnyNode && sceneObject.transform !== entity) {
                        return;
                    }
                    const viewer = getViewerElement();
                    viewer?.traceScene?.select(sceneObject);
                }
            }
        }

        const inspectorObserver = selectionService.onSelectedEntityChanged.add(syncInspectorToViewport);

        window.addEventListener('trace-viewer:selection-changed', handleViewportSelection);

        return {
            dispose: () => {
                window.removeEventListener('trace-viewer:selection-changed', handleViewportSelection);
                window.removeEventListener('trace-viewer:playmode-changed', handlePlayModeChanged);
                inspectorObserver.remove();
                selectionServiceRef = null;
            }
        };
    }
};
